<script setup lang="ts">
import DiagnosticsTableRow from '@/components/rc/miner-repairs/repairs-sidebar/diagnostics-elements/DiagnosticsTableRow.vue'
import { useMinerDetails } from '@/composables/rc/miner-details'
import { useMinerRepairActions } from '@/composables/rc/miner-repair-actions'
import { HashboardParts, minerDetailsParts } from '@/types/rc/miners'
import { PartDiagnosticStatus, PartStatus } from '@/types/rc/miners/PartStatus'
import type { IndividualPart } from '@/types/rc/miners/PartType'
import { h, ref, toRefs } from 'vue'

const props = defineProps<{ minerId: number }>()
const { minerId } = toRefs(props)

const { updatePartStatusMutation } = useMinerRepairActions(minerId)
const { minerDetailsQuery } = useMinerDetails(minerId)
const { data: minerDetails } = minerDetailsQuery

const flaggingFail = ref<IndividualPart | null>(null)

const onFlaggingFail = (isFlaggingFail: boolean, partName: IndividualPart) => {
  flaggingFail.value = isFlaggingFail ? partName : null
}

const onFlagPartGood = (partId: number, partName: IndividualPart) => {
  updatePartStatusMutation.mutate({
    part_id: partId,
    partName,
    status: PartStatus.Good,
    initial_diagnostic_data: { initial_diagnostic: PartDiagnosticStatus.Good },
  })
}

const onFlagPartFail = (
  partId: number,
  partName: IndividualPart,
  failureType: number,
  failureReason?: string,
  discardNotes: () => void = () => {} // If there is failure reason
) => {
  updatePartStatusMutation.mutate(
    {
      part_id: partId,
      partName,
      status: PartStatus.Fail,
      initial_diagnostic_data: {
        initial_diagnostic: PartDiagnosticStatus.Fail,
        failure_type_id: failureType,
        comment: failureReason,
      },
    },
    { onError: discardNotes }
  )
}

// Render buttons grid by flat-rendering all parts into a single array
const Grid = () =>
  h(
    'div',
    { class: 'diagnostics-table' },
    (minerDetails.value?.hashboard_only ? HashboardParts : minerDetailsParts).flatMap((partName) =>
      h(DiagnosticsTableRow, {
        minerId: props.minerId,
        disabled: flaggingFail.value != null && flaggingFail.value != partName,
        partName,
        onFlagPartGood,
        onFlagPartFail,
        onFlaggingFail,
      })
    )
  )
</script>

<template>
  <Grid />
</template>
